/**
 * Typed wrappers around the JSON API. Hand-written rather than generated to
 * keep the dependency surface small. Each module groups one resource family.
 */
import { apiClient } from './apiClient';
import type { UserRole } from './auth';

export interface JobSite {
  id: string;
  name: string;
  address: string | null;
  active: boolean | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  gps_location: unknown;
  notes: string | null;
  has_rpz: boolean | null;
  aspire_property_id: number | null;
  created_at: string;
}

export interface Controller {
  id: string;
  job_site_id: string;
  controller_number: number;
  description: string | null;
  location: string | null;
  notes: string | null;
  photo_url: string | null;
  manufacturer_id: string | null;
  model: string | null;
  serial_number: string | null;
  water_days_of_week: string[] | null;
  water_start_times: unknown;
  gps_location: unknown;
  created_at: string;
}

export interface Zone {
  id: string;
  controller_id: string;
  zone_number: number;
  type: string;
  photo_urls: string[] | null;
  gps_location: unknown;
  created_at: string;
}

export interface WaterMeter {
  id: string;
  job_site_id: string;
  size: string;
  meter_number: string;
  location: string;
  is_rpz: boolean;
  gps_location: unknown;
  photo_url: string | null;
  notes: string | null;
  created_at: string;
}

export interface DoubleCheck {
  id: string;
  job_site_id: string;
  description: string;
  size: string;
  gps_location: unknown;
  photo_url: string | null;
  notes: string | null;
  created_at: string;
}

export interface InspectionReport {
  id: string;
  job_site_name: string;
  job_site_id: string | null;
  month: string;
  technicians: string[];
  is_draft: boolean | null;
  status_id: string | null;
  user_id: string | null;
  manager_notes: string | null;
  rejection_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  email_sent_at: string | null;
  email_sent_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface PickedUser {
  oid: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
}

/**
 * Admin user-list row — mirrors the raw `user_profiles` row the server
 * returns from `GET /api/users` (snake_case). This intentionally does NOT
 * extend `UserProfile` from `auth.ts`: that type is the camelCase shape of
 * `/api/auth/me`, but the admin list endpoint hands back the unmapped DB row.
 */
export interface AdminUserRow {
  id: string;
  entra_id: string;
  email: string;
  display_name: string;
  role: UserRole;
  preferred_locale: string | null;
  photo_url: string | null;
  is_active: boolean;
  deactivated_at: string | null;
  aspire_crew_leader_contact_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface AspireCrewLeader {
  contactId: number;
  name: string;
}

// Re-export the shared role type so consumers don't need two imports.
export type { UserRole } from './auth';

const crud = <T>(base: string) => ({
  list: () => apiClient.get<T[]>(base),
  get: (id: string) => apiClient.get<T>(`${base}/${id}`),
  create: (body: Partial<T>) => apiClient.post<T>(base, body),
  update: (id: string, body: Partial<T>) => apiClient.put<T>(`${base}/${id}`, body),
  remove: (id: string) => apiClient.del<void>(`${base}/${id}`),
});

export const jobSitesApi = crud<JobSite>('/api/job-sites');
export const controllersApi = crud<Controller>('/api/controllers');
export const zonesApi = crud<Zone>('/api/zones');
export const waterMetersApi = crud<WaterMeter>('/api/water-meters');
export const doubleChecksApi = crud<DoubleCheck>('/api/double-checks');

export const reportsApi = {
  ...crud<InspectionReport>('/api/reports'),
  submit: (id: string) => apiClient.post<InspectionReport>(`/api/reports/${id}/submit`),
  approve: (id: string) => apiClient.post<InspectionReport>(`/api/reports/${id}/approve`),
  reject: (id: string, reason: string) =>
    apiClient.post<InspectionReport>(`/api/reports/${id}/reject`, { reason }),
};

export const usersApi = {
  list: () => apiClient.get<AdminUserRow[]>('/api/users'),
  picker: (q: string) =>
    apiClient.get<PickedUser[]>(`/api/users/picker?q=${encodeURIComponent(q)}`),
  create: (input: {
    entraId: string;
    email: string;
    displayName: string;
    role: UserRole;
  }) => apiClient.post<AdminUserRow>('/api/users', input),
  updateRole: (id: string, role: UserRole) =>
    apiClient.put<AdminUserRow>(`/api/users/${id}`, { role }),
  updateCrewLeader: (id: string, aspire_crew_leader_contact_id: number | null) =>
    apiClient.put<AdminUserRow>(`/api/users/${id}`, { aspire_crew_leader_contact_id }),
  remove: (id: string) => apiClient.del<void>(`/api/users/${id}`),
};

export const aspireApi = {
  crewLeaders: () =>
    apiClient
      .get<{ crewLeaders: AspireCrewLeader[] }>('/api/integrations/aspire/crew-leaders')
      .then((r) => r.crewLeaders ?? []),
};
